import { callingIdSchema, townIdSchema } from '@wyn/content';
import { z } from 'zod';
import { sanctionSchema } from './schemas.ts';
import { chatChannelSchema, chatMessageSchema, reportReasonSchema } from './social.ts';

// ---- The stewards' view (/v1/admin). Every route needs the `admins` Cognito group. ----

export const SANCTION_DURATIONS = ['1h', '24h', '7d', 'permanent'] as const;
export const sanctionDurationSchema = z.enum(SANCTION_DURATIONS);
export type SanctionDuration = z.infer<typeof sanctionDurationSchema>;
/** How long each lasts, in ms; null is for good. */
export const SANCTION_DURATION_MS: Record<SanctionDuration, number | null> = { '1h': 3_600_000, '24h': 86_400_000, '7d': 604_800_000, permanent: null };
export const SANCTION_DURATION_LABELS: Record<SanctionDuration, string> = { '1h': '1 hour', '24h': '24 hours', '7d': '7 days', permanent: 'For good' };

const reasonSchema = z.string().trim().min(1).max(200);

export const REPORT_OUTCOMES = ['actioned', 'no-action'] as const;
export const reportOutcomeSchema = z.enum(REPORT_OUTCOMES);
export type ReportOutcome = z.infer<typeof reportOutcomeSchema>;

export const MODLOG_ACTIONS = ['remove-line', 'mute', 'ban', 'lift', 'rename', 'resolve'] as const;

/** One entry in a player's moderation log: who did what to them, why, and on which report. */
export const modlogEntrySchema = z.object({
  id: z.string(),
  at: z.number(),
  playerId: z.string(),
  /** The steward's Cognito sub. */
  by: z.string(),
  action: z.enum(MODLOG_ACTIONS),
  reason: z.string(),
  reportId: z.string().nullable(),
  /** What else the action needs remembered: a sanction's end, the removed line, a report's outcome. */
  detail: z.record(z.string(), z.unknown()),
});
export type ModlogEntry = z.infer<typeof modlogEntrySchema>;

/** A report as the stewards see it, with its own copy of the chat around a reported line. */
export const reportViewSchema = z.object({
  id: z.string(),
  at: z.number(),
  reporterId: z.string(),
  reporterName: z.string(),
  targetId: z.string(),
  /** The name the target had when reported. */
  targetName: z.string(),
  reason: reportReasonSchema,
  note: z.string().nullable(),
  chat: z.object({ channel: z.string(), line: chatMessageSchema, before: z.array(chatMessageSchema), after: z.array(chatMessageSchema) }).nullable(),
});
export type ReportView = z.infer<typeof reportViewSchema>;

/** The reported player as they stand now. */
export const adminPlayerSchema = z.object({
  id: z.string(),
  name: z.string(),
  calling: callingIdSchema,
  town: townIdSchema,
  createdAt: z.number(),
  resident: z.boolean(),
  sanction: sanctionSchema.nullable(),
  renameRequired: z.boolean(),
});
export type AdminPlayer = z.infer<typeof adminPlayerSchema>;

// GET /v1/admin/reports — open reports, oldest first.
export const adminReportsResponseSchema = z.object({
  reports: z.array(
    z.object({
      report: reportViewSchema,
      /** Null if the player row has gone. */
      target: adminPlayerSchema.nullable(),
      /** Open reports against the same player, this one included. */
      openAgainst: z.number().int(),
      /** The target's moderation log, newest first. */
      history: z.array(modlogEntrySchema),
    }),
  ),
});
export type AdminReportsResponse = z.infer<typeof adminReportsResponseSchema>;

/** One of a player's events, in their own words, with the other player in it (a fight, a trade, a market fill), if any. */
export const adminEventSchema = z.object({ id: z.string(), at: z.number(), type: z.string(), text: z.string(), otherId: z.string().nullable() });
export type AdminEvent = z.infer<typeof adminEventSchema>;

/** One movement of coin or goods. `counterparty` is `SYSTEM`, `SELF`, `BANK`, `MARKET`, `NPC:*` or `PLAYER:<id>`. */
export const adminLedgerRowSchema = z.object({
  id: z.string(),
  at: z.number(),
  kind: z.enum(['coin', 'item']),
  account: z.string().nullable(),
  itemKey: z.string().nullable(),
  quality: z.string().nullable(),
  delta: z.number(),
  reason: z.string(),
  counterparty: z.string(),
});
export type AdminLedgerRow = z.infer<typeof adminLedgerRowSchema>;

/** A report a player made or was reported in, open or resolved (kept 90 days after). */
export const adminReportSummarySchema = z.object({
  id: z.string(),
  at: z.number(),
  reporterId: z.string(),
  reporterName: z.string(),
  targetId: z.string(),
  targetName: z.string(),
  reason: reportReasonSchema,
  note: z.string().nullable(),
  /** The reported line's words, for a chat report. */
  line: z.string().nullable(),
  resolution: z.object({ outcome: reportOutcomeSchema, note: z.string().nullable(), at: z.number() }).nullable(),
});
export type AdminReportSummary = z.infer<typeof adminReportSummarySchema>;

// GET /v1/admin/player?q= — by name (or id): sanctions, the moderation log, recent chat, and what they have been doing.
export const adminPlayerRequestSchema = z.object({ q: z.string().trim().min(1).max(40) });
export const adminPlayerResponseSchema = z.object({
  player: adminPlayerSchema,
  modlog: z.array(modlogEntrySchema),
  /** Their lines still in chat (30 days at most), newest first. */
  chat: z.array(chatMessageSchema),
  /** Their latest events (kept 90 days), newest first. */
  events: z.array(adminEventSchema),
  /** Their latest movements of coin and goods, newest first. */
  ledger: z.array(adminLedgerRowSchema),
  /** Reports they made or were reported in, newest first. */
  reports: z.array(adminReportSummarySchema),
  /** The players they have blocked, oldest first. */
  blocks: z.array(z.object({ id: z.string(), name: z.string(), at: z.number() })),
  /** The names of the other players the events and ledger mention, by id. */
  names: z.record(z.string(), z.string()),
});
export type AdminPlayerResponse = z.infer<typeof adminPlayerResponseSchema>;

// GET /v1/admin/flags — trades between players on one device (M5), newest first.
export const tradeFlagSchema = z.object({
  id: z.string(),
  at: z.number(),
  offerId: z.string(),
  from: z.object({ id: z.string(), name: z.string() }),
  to: z.object({ id: z.string(), name: z.string() }),
  give: z.unknown(),
  want: z.unknown(),
});
export const adminFlagsResponseSchema = z.object({ flags: z.array(tradeFlagSchema) });
export type AdminFlagsResponse = z.infer<typeof adminFlagsResponseSchema>;

// GET /v1/admin/between?a=&b= — what has passed between two players: blocks, reports either way, and the events with both in them.
export const adminBetweenRequestSchema = z.object({ a: z.string().min(1).max(40), b: z.string().min(1).max(40) });
export const adminBetweenResponseSchema = z.object({
  blocks: z.object({ aBlockedB: z.boolean(), bBlockedA: z.boolean() }),
  reports: z.array(adminReportSummarySchema),
  /** From both players' logs (kept 90 days), newest first. `of` is whose log it is from. */
  events: z.array(adminEventSchema.extend({ of: z.string() })),
});
export type AdminBetweenResponse = z.infer<typeof adminBetweenResponseSchema>;

const onReport = { reportId: z.string().max(40).optional() };

// POST /v1/admin/chat/remove — the line's text is cleared and every open Talk page shows it removed.
export const adminRemoveLineRequestSchema = z.object({ channel: chatChannelSchema, lineId: z.string().min(1).max(40), reason: reasonSchema, ...onReport });
export const adminRemoveLineResponseSchema = z.object({ removed: chatMessageSchema });

// POST /v1/admin/sanction — mute or ban, replacing any sanction in force.
export const adminSanctionRequestSchema = z.object({
  playerId: z.string().min(1).max(40),
  kind: z.enum(['mute', 'ban']),
  duration: sanctionDurationSchema,
  reason: reasonSchema,
  ...onReport,
});
export const adminSanctionResponseSchema = z.object({ sanction: sanctionSchema.nullable() });

// POST /v1/admin/sanction/lift
export const adminLiftRequestSchema = z.object({ playerId: z.string().min(1).max(40), reason: reasonSchema, ...onReport });

// POST /v1/admin/rename — releases the name; the player must choose another before doing anything else.
export const adminRenameRequestSchema = z.object({ playerId: z.string().min(1).max(40), reason: reasonSchema, ...onReport });
export const adminRenameResponseSchema = z.object({ released: z.string() });

// POST /v1/admin/resolve — closes a report; it is deleted 90 days later.
export const adminResolveRequestSchema = z.object({ reportId: z.string().min(1).max(40), outcome: reportOutcomeSchema, note: z.string().trim().max(200).optional() });
export const adminResolveResponseSchema = z.object({ ok: z.literal(true) });

// ---- The realm's economy, by day (/v1/admin/realm) ----

/** One UTC day's coin flows, added up from the ledger. Sinks are negative; `faucets + sinks` is the change in supply. */
export const dayFlowsSchema = z.object({
  day: z.string(),
  faucets: z.number(),
  sinks: z.number(),
  /** Coin that changed hands between players (and through market escrow), counted once. */
  transfers: z.number(),
  characters: z.number(),
  /** By flow and reason family: `faucet:minigame`, `sink:shop`. */
  byReason: z.record(z.string(), z.number()),
});
export type DayFlowsView = z.infer<typeof dayFlowsSchema>;

export const REALM_DAYS = 30;
export const adminRealmResponseSchema = z.object({ days: z.array(dayFlowsSchema) });
export type AdminRealmResponse = z.infer<typeof adminRealmResponseSchema>;
